/*
 * HomeHero — the new post-login landing page.
 *
 * Purpose: keep the initial view calm and welcoming. Detailed affiliate
 * network data lives on the /affiliate tab; detailed MT5 metrics live on the
 * /trading tab. This page shows only:
 *   • 3D "Welcome to VeloxAlgoTech LLP" hero
 *   • Company description
 *   • Compact MT5 account status (both accounts) with a CTA to open the
 *     dedicated Trading Dashboard
 *   • A single "Enter Affiliate Dashboard" CTA
 *   • Quick reports (Total Withdrawn + Registrations)
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import {
  ArrowRight, ChartLine, Users, Wallet, CheckCircle, Warning, Clock,
  Lock, Coins, CurrencyDollar,
} from "@phosphor-icons/react";

const ACCENTS = {
  cent: { icon: Coins, color: "#F59E0B", label: "CENT Account" },
  usd: { icon: CurrencyDollar, color: "#10B981", label: "USD Account" },
};

function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / 86400000);
}

export default function HomeHero({ profile, onNavigate }) {
  const [accounts, setAccounts] = useState({ cent: null, usd: null });
  const [stats, setStats] = useState({ total_withdrawn: 0, registrations_count: 0 });

  useEffect(() => {
    axios
      .get(`${AFF_API}/affiliate/mt5/accounts`, { headers: getAffHeaders() })
      .then((r) => setAccounts(r.data.accounts || { cent: null, usd: null }))
      .catch(() => {});
    axios
      .get(`${AFF_API}/affiliate/quick-reports`, { headers: getAffHeaders() })
      .then((r) => setStats(r.data || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-10" data-testid="home-hero">
      {/* ==================== HERO ==================== */}
      <section className="relative overflow-hidden rounded-2xl px-6 py-14 md:px-14 md:py-20" style={{ background: "linear-gradient(180deg, var(--velox-panel) 0%, var(--velox-bg-elevated) 100%)", border: "1px solid var(--velox-border-strong)", backdropFilter: "blur(24px)" }}>
        {/* Ambient background — refined corporate glass */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-[#00b4d8]/8 blur-[140px]" />
          <div className="absolute -bottom-40 -right-32 w-[620px] h-[620px] rounded-full bg-blue-600/10 blur-[150px]" />
          {/* Subtle grid + noise */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
            }}
          />
          {/* Top-edge highlight (glass reflection) */}
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>

        <div className="relative z-10 max-w-4xl">
          {/* Brand lockup — small monogram + wordmark + regulated pill */}
          <div className="flex items-center gap-3 mb-8" data-testid="hero-brand-lockup">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/15 bg-white/5">
              <img
                src="https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png"
                alt="VeloxAlgoTech LLP"
                className="w-6 h-6"
              />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[13px] font-bold tracking-wide text-white" style={{ fontFamily: "'Cabinet Grotesk', 'Manrope', sans-serif" }}>
                VeloxAlgoTech LLP
              </span>
              <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-zinc-500">
                Quantitative Trading Systems
              </span>
            </div>
            <div className="ml-auto hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/8 border border-emerald-500/25">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-300">Systems Live</span>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-300">
              Welcome back, {profile?.name?.split(" ")[0] || "Trader"}
            </span>
          </div>

          {/* 3D typography title — layered text-shadows with cyan → blue gradient */}
          <h1
            className="hero-3d-title font-black leading-[0.9] tracking-tight mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', 'Manrope', sans-serif" }}
            data-testid="home-hero-title"
          >
            <span className="hero-3d-line hero-3d-anim block">Welcome to</span>
            <span className="hero-3d-line hero-3d-brand hero-3d-anim-2 block">VeloxAlgoTech LLP</span>
          </h1>

          <p className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-3xl mb-8" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
            VeloxAlgoTech LLP is a premier fintech company dedicated to providing
            high-performance, high-timeframe market analysis. Our systems
            empower traders with precise technical insights to navigate global
            markets seamlessly.
          </p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => onNavigate("trading")}
              data-testid="hero-cta-trading"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold transition-all"
              style={{
                background: "var(--velox-accent-emerald)",
                color: "#0b132b",
                boxShadow: "0 10px 40px -15px rgba(6,214,160,0.6)",
              }}
            >
              <ChartLine size={14} weight="bold" />
              Open Trading Dashboard
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => onNavigate("affiliate")}
              data-testid="hero-cta-affiliate"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-all"
              style={{
                background: "var(--velox-panel-2)",
                color: "var(--velox-accent-cyan)",
                border: "1px solid var(--velox-border-strong)",
              }}
            >
              <Users size={14} weight="bold" />
              Affiliate Dashboard
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        {/* Executive status strip — bottom of hero */}
        <div className="relative z-10 mt-10 md:mt-14 pt-6 border-t border-white/8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="hero-status-strip">
          <StatusCell label="Platform Uptime" value="99.98%" sub="Rolling 30d" tone="emerald" />
          <StatusCell label="Master Signals" value="Streaming" sub="24 × 7 · CopyFactory" tone="cyan" />
          <StatusCell label="Data Region" value="AP-South-1" sub="Mumbai · GST-compliant" tone="zinc" />
          <StatusCell label="Support Window" value="09:00 – 21:00 IST" sub="Mon – Sat" tone="zinc" />
        </div>

        <style>{`
          .hero-3d-title { font-size: clamp(2.5rem, 7vw, 5.5rem); perspective: 1200px; }
          .hero-3d-line {
            display: inline-block;
            transform-style: preserve-3d;
            background: linear-gradient(180deg, #FFFFFF 0%, #A1A1AA 100%);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            text-shadow:
              0 1px 0 rgba(255,255,255,0.05),
              0 2px 0 rgba(0,0,0,0.4),
              0 4px 0 rgba(0,0,0,0.35),
              0 6px 0 rgba(0,0,0,0.3),
              0 8px 12px rgba(0,180,216,0.15),
              0 14px 40px rgba(0,180,216,0.25);
          }
          .hero-3d-brand {
            background: linear-gradient(180deg, #00b4d8 0%, #0077b6 55%, #023e8a 100%);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            text-shadow:
              0 1px 0 rgba(0,180,216,0.15),
              0 2px 0 rgba(0,0,0,0.5),
              0 4px 0 rgba(0,0,0,0.4),
              0 6px 0 rgba(0,0,0,0.35),
              0 10px 20px rgba(0,180,216,0.35),
              0 20px 50px rgba(0,119,182,0.35);
            letter-spacing: -0.02em;
          }

          /* Continuous 3D float + tilt animation */
          .hero-3d-anim {
            animation: hero-float 6s ease-in-out infinite,
                       hero-tilt 9s ease-in-out infinite,
                       hero-fade-up 900ms cubic-bezier(0.22, 1, 0.36, 1) both;
          }
          .hero-3d-anim-2 {
            animation: hero-float 6s ease-in-out infinite 0.4s,
                       hero-tilt 9s ease-in-out infinite 0.6s,
                       hero-fade-up 1100ms cubic-bezier(0.22, 1, 0.36, 1) both 0.15s,
                       hero-glow 3.5s ease-in-out infinite 1s;
          }
          @keyframes hero-float {
            0%, 100% { transform: translateZ(0) translateY(0); }
            50%      { transform: translateZ(30px) translateY(-6px); }
          }
          @keyframes hero-tilt {
            0%, 100% { transform: rotateX(0deg) rotateY(0deg); }
            25%      { transform: rotateX(2deg)  rotateY(-3deg); }
            50%      { transform: rotateX(0deg)  rotateY(3deg);  }
            75%      { transform: rotateX(-2deg) rotateY(-2deg); }
          }
          @keyframes hero-fade-up {
            from { opacity: 0; transform: translateY(28px) scale(0.96) rotateX(-8deg); filter: blur(6px); }
            to   { opacity: 1; transform: translateY(0)    scale(1)    rotateX(0);     filter: blur(0);  }
          }
          @keyframes hero-glow {
            0%, 100% {
              text-shadow:
                0 1px 0 rgba(0,180,216,0.15),
                0 2px 0 rgba(0,0,0,0.5),
                0 4px 0 rgba(0,0,0,0.4),
                0 6px 0 rgba(0,0,0,0.35),
                0 10px 20px rgba(0,180,216,0.35),
                0 20px 50px rgba(0,119,182,0.35);
            }
            50% {
              text-shadow:
                0 1px 0 rgba(0,180,216,0.25),
                0 2px 0 rgba(0,0,0,0.5),
                0 4px 0 rgba(0,0,0,0.4),
                0 6px 0 rgba(0,0,0,0.35),
                0 14px 30px rgba(0,180,216,0.55),
                0 28px 70px rgba(0,119,182,0.55);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .hero-3d-anim, .hero-3d-anim-2 { animation: none; }
          }
        `}</style>
      </section>

      {/* ==================== MT5 SUMMARY (compact) ==================== */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ChartLine size={16} weight="duotone" className="text-[#00b4d8]" />
            <h2 className="text-sm font-bold text-white tracking-wide">Your Trading Accounts</h2>
          </div>
          <button
            onClick={() => onNavigate("trading")}
            data-testid="hero-view-trading-link"
            className="text-[11px] font-semibold text-[#00b4d8] hover:text-white transition-colors inline-flex items-center gap-1"
          >
            Open Trading Dashboard <ArrowRight size={11} weight="bold" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(["cent", "usd"]).map((t) => (
            <MiniAccountCard
              key={t}
              type={t}
              acct={accounts[t]}
              onOpen={() => onNavigate("trading")}
            />
          ))}
        </div>
      </section>

      {/* ==================== QUICK REPORTS ==================== */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-white tracking-wide">Quick Reports</h2>
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Last 30 days</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ReportTile
            icon={Wallet}
            label="Total Withdrawn"
            value={`₹${(stats.total_withdrawn || 0).toLocaleString("en-IN")}`}
            sub="Per period"
            color="#F59E0B"
            testid="report-withdrawn"
          />
          <ReportTile
            icon={Users}
            label="Registrations"
            value={String(stats.registrations_count || 0)}
            sub="Per period (across network)"
            color="#00b4d8"
            testid="report-registrations"
          />
        </div>
      </section>

      {/* ==================== INDIAN MARKET ENTRY POINT ==================== */}
      <section
        className="rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap"
        style={{
          background: "linear-gradient(135deg, rgba(0,180,216,0.08) 0%, rgba(6,214,160,0.05) 100%)",
          border: "1px solid var(--velox-border)",
        }}
        data-testid="hero-indian-market-cta"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "rgba(0,180,216,0.15)", border: "1px solid rgba(0,180,216,0.3)" }}
          >
            <ChartLine size={18} weight="duotone" style={{ color: "#00b4d8" }} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-white">Trading the Indian Market?</p>
              <span
                className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider"
                style={{ background: "#F59E0B", color: "#0b132b" }}
              >
                NEW
              </span>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
              Get your Veloxalgotech HTF indicator setup for Banknifty, plus the TRADER mindset guide.
            </p>
          </div>
        </div>
        <button
          onClick={() => onNavigate("indian-market")}
          data-testid="hero-goto-indian-market"
          className="px-4 py-2 rounded-md text-xs font-bold inline-flex items-center gap-1.5 transition-all"
          style={{ background: "#00b4d8", color: "#0b132b" }}
        >
          Open Indian Market
          <ArrowRight size={12} weight="bold" />
        </button>
      </section>
    </div>
  );
}

function MiniAccountCard({ type, acct, onOpen }) {
  const meta = ACCENTS[type];
  const Icon = meta.icon;
  const linked = !!acct;
  const remaining = daysLeft(acct?.expires_at);
  const isActive = ["active", "verified", "active_unverified"].includes(acct?.status);
  const isExpired = acct?.status === "expired";

  let badge;
  if (!linked) {
    badge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-white/5 text-zinc-400 border border-white/10">
        <Lock size={10} weight="bold" /> Not linked
      </span>
    );
  } else if (isActive && remaining != null && remaining > 7) {
    badge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
        <CheckCircle size={10} weight="bold" /> Active
      </span>
    );
  } else if (isActive && remaining != null && remaining <= 7 && remaining > 0) {
    badge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30">
        <Clock size={10} weight="bold" /> {remaining}d left
      </span>
    );
  } else if (isExpired) {
    badge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-rose-500/15 text-rose-300 border border-rose-500/30">
        <Warning size={10} weight="bold" /> Expired
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`mini-acct-${type}`}
      className="group text-left p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04] transition-all"
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: `${meta.color}1A` }}
        >
          <Icon size={18} weight="duotone" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-white">{meta.label}</span>
            {badge}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
            {linked ? `${acct.broker_name} · ${acct.mt5_server}` : "Not activated yet"}
          </div>
        </div>
        <ArrowRight
          size={14}
          className="text-zinc-600 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0"
        />
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">MT5 Login</span>
        <span className="text-base font-black text-white font-mono">
          {linked ? acct.mt5_login_id : "—"}
        </span>
      </div>
    </button>
  );
}

function ReportTile({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-5 rounded-xl border border-white/10 bg-white/[0.02]"
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} weight="duotone" style={{ color }} />
        <span className="text-xs font-semibold text-zinc-300">{label}</span>
      </div>
      <div className="text-3xl font-black text-white font-mono tracking-tight">{value}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}

// Compact metric cell used in the executive status strip below the hero.
// Corporate-premium look: no big icons, tight typography, tone-tinted labels.
function StatusCell({ label, value, sub, tone = "zinc" }) {
  const tones = {
    emerald: "text-emerald-300",
    cyan:    "text-[#7DD3FC]",
    zinc:    "text-white",
  };
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{label}</div>
      <div className={`text-sm font-bold ${tones[tone] || tones.zinc}`}>{value}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}

